import React, { useState, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Form, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './ChartSensores.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

function ChartSensores({ dados }) {
  const [filtroTempo, setFiltroTempo] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [horaInicio, setHoraInicio] = useState('00:00');
  const [horaFim, setHoraFim] = useState('23:59');
  // Evita logs ruidosos em produção
  // console.debug('ChartSensores dados:', dados);
  

  // Normaliza datas vindas do backend para evitar deslocamento de -3h
  // Cenário comum: backend envia horário local marcado como UTC ("Z" ou +00:00),
  // resultando em exibição -3h em browsers no fuso de Brasília. Esta função
  // detecta timestamps em UTC puro (Z, +00:00, -00:00) e aplica a compensação
  // do fuso local para preservar a intenção de "horário local".
  const parseDateNormalized = (input) => {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return null;
    if (typeof input === 'string') {
      const tz = input.match(/([+-]\d{2}:\d{2}|Z)$/)?.[1] || '';
      // Ajustar apenas quando o timestamp vier explicitamente como UTC puro
      if (tz === 'Z' || tz === '+00:00' || tz === '-00:00') {
        const adjustMinutes = new Date().getTimezoneOffset(); // ex.: 180 para UTC-03
        return new Date(d.getTime() + adjustMinutes * 60 * 1000);
      }
    }
    return d;
  };

  // Constrói uma data no fuso local a partir de 'YYYY-MM-DD' e hora/minuto.
  // Evita o comportamento de o Date interpretar 'YYYY-MM-DD' como UTC.
  const buildLocalDate = (dateStr, h = 0, m = 0, s = 0, ms = 0) => {
    if (!dateStr) return null;
    const [Y, M, D] = dateStr.split('-').map(Number);
    if (!Y || !M || !D) return null;
    return new Date(Y, M - 1, D, h, m, s, ms);
  };

  // Filtrar dados por data
  const dadosFiltrados = useMemo(() => {
    if (!Array.isArray(dados) || dados.length === 0) return [];

    // Atalhos: 24h, 7d, 30d
    if (filtroTempo === '24h' || filtroTempo === '7d' || filtroTempo === '30d') {
      const agora = new Date();
      const dias = filtroTempo === '24h' ? 1 : filtroTempo === '7d' ? 7 : 30;
      const inicio = new Date(agora.getTime() - dias * 24 * 60 * 60 * 1000);
      return dados.filter(item => {
        const d = parseDateNormalized(item?.date);
        return d && d >= inicio && d <= agora;
      });
    }

    // Personalizado
    if (filtroTempo === 'custom' && dataInicio && dataFim && horaInicio && horaFim) {
      const [hIni, mIni] = horaInicio.split(':').map(Number);
      const [hFim, mFim] = horaFim.split(':').map(Number);
      const inicio = buildLocalDate(dataInicio, hIni, mIni, 0, 0);
      const fim = buildLocalDate(dataFim, hFim, mFim, 59, 999);
      if (!inicio || !fim) return [];
      return dados.filter(item => {
        const d = parseDateNormalized(item?.date);
        return d && d >= inicio && d <= fim;
      });
    }

    // Sem filtro definido
    return dados;
  }, [dados, filtroTempo, dataInicio, dataFim, horaInicio, horaFim]);

  // Texto do período exibido no gráfico
  const periodoExibido = useMemo(() => {
    if (!dadosFiltrados || dadosFiltrados.length === 0) return '';

    // Se filtro personalizado e datas preenchidas, usar exatamente esse intervalo
    if (filtroTempo === 'custom' && dataInicio && dataFim && horaInicio && horaFim) {
      const [hIni, mIni] = horaInicio.split(':').map(Number);
      const inicio = buildLocalDate(dataInicio, hIni, mIni, 0, 0);
      const [hF, mF] = horaFim.split(':').map(Number);
      const fim = buildLocalDate(dataFim, hF, mF, 59, 999);
      if (!inicio || !fim) return '';
      return `${inicio.toLocaleString('pt-BR')} — ${fim.toLocaleString('pt-BR')}`;
    }

    // Caso contrário, derivar das datas dos dados filtrados
    const datas = dadosFiltrados
      .map(item => parseDateNormalized(item.date))
      .filter(d => d && !Number.isNaN(d.getTime()));
    if (datas.length === 0) return '';
    const min = new Date(Math.min(...datas));
    const max = new Date(Math.max(...datas));

    if (min.toDateString() === max.toDateString()) {
      const horaMin = min.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const horaMax = max.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      return `${min.toLocaleDateString('pt-BR')} • ${horaMin} — ${horaMax}`;
    }

    return `${min.toLocaleString('pt-BR')} — ${max.toLocaleString('pt-BR')}`;
  }, [dadosFiltrados, filtroTempo, dataInicio, dataFim, horaInicio, horaFim]);

  // ====== Utilidades e derivados antes de qualquer retorno condicional ======
  // Função para média ignorando null/undefined e valores negativos
  const media = useMemo(() => (arr => {
    const filtrado = arr.filter(v => v !== null && v !== undefined && v >= 0);
    if (filtrado.length === 0) return null;
    return filtrado.reduce((a, b) => a + b, 0) / filtrado.length;
  }), []);

  // Número de sensores baseado no primeiro item filtrado
  const sensoresCount = useMemo(() => {
    return dadosFiltrados[0]?.sensores?.length || 0;
  }, [dadosFiltrados]);

  // Identificar sensores com dados válidos (pelo menos uma das médias TA/TG/UR)
  const sensoresComDados = useMemo(() => {
    const indices = [];
    for (let i = 0; i < sensoresCount; i++) {
      const taVals = dadosFiltrados.map(item => item.sensores[i]?.ta ?? null);
      const tgVals = dadosFiltrados.map(item => item.sensores[i]?.tg ?? null);
      const urVals = dadosFiltrados.map(item => item.sensores[i]?.ur ?? null);
      const taMedia = media(taVals);
      const tgMedia = media(tgVals);
      const urMedia = media(urVals);
      if (taMedia !== null || tgMedia !== null || urMedia !== null) {
        indices.push(i);
      }
    }
    return indices;
  }, [dadosFiltrados, sensoresCount, media]);

  // Ordenar por data crescente para os gráficos de linha
  const dadosOrdenadosAsc = useMemo(() => {
    const arr = (dadosFiltrados || []).slice();
    arr.sort((a, b) => {
      const ta = parseDateNormalized(a?.date)?.getTime() ?? 0;
      const tb = parseDateNormalized(b?.date)?.getTime() ?? 0;
      return ta - tb;
    });
    return arr;
  }, [dadosFiltrados]);

  // Limite máximo de pontos por gráfico de linha
  const MAX_PONTOS = 1000;

  // Agregar dados em janelas quando exceder MAX_PONTOS, calculando médias
  const baseDados = useMemo(() => {
    const n = dadosOrdenadosAsc?.length || 0;
    if (n === 0) return [];
    if (n <= MAX_PONTOS) return dadosOrdenadosAsc;

    const bucketSize = Math.ceil(n / MAX_PONTOS);
    const result = [];

    for (let i = 0; i < n; i += bucketSize) {
      const group = dadosOrdenadosAsc.slice(i, i + bucketSize);
      if (group.length === 0) continue;

      // Usar a última data do grupo para manter a sensação de continuidade temporal
      const lastItem = group[group.length - 1];
      const sensoresLen = sensoresCount;

      const sensoresAgregados = Array.from({ length: sensoresLen }, (_, idx) => {
        const taVals = group
          .map(g => g.sensores?.[idx]?.ta)
          .filter(v => v !== null && v !== undefined && v >= 0);
        const tgVals = group
          .map(g => g.sensores?.[idx]?.tg)
          .filter(v => v !== null && v !== undefined && v >= 0);
        const urVals = group
          .map(g => g.sensores?.[idx]?.ur)
          .filter(v => v !== null && v !== undefined && v >= 0);

        const avg = (arr) => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;

        return {
          ta: avg(taVals),
          tg: avg(tgVals),
          ur: avg(urVals),
        };
      });

      result.push({
        ...lastItem,
        date: lastItem.date, // preservar string original
        sensores: sensoresAgregados,
      });
    }

    return result;
  }, [dadosOrdenadosAsc, sensoresCount]);

  // Labels de tempo e séries por sensor para gráficos de linha
  const timeLabels = useMemo(() => {
    return baseDados
      .map(item => parseDateNormalized(item.date))
      .filter(d => d && !Number.isNaN(d.getTime()));
  }, [baseDados]);

  const formattedLabels = useMemo(() => {
    return timeLabels.map(d => d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    }));
  }, [timeLabels]);

  const seriesPorSensor = useMemo(() => {
    return sensoresComDados.map(sensorIndex => {
      const ta = baseDados.map(item => {
        const v = item.sensores[sensorIndex]?.ta;
        return (v !== null && v !== undefined && v >= 0) ? v : null;
      });
      const tg = baseDados.map(item => {
        const v = item.sensores[sensorIndex]?.tg;
        return (v !== null && v !== undefined && v >= 0) ? v : null;
      });
      const ur = baseDados.map(item => {
        const v = item.sensores[sensorIndex]?.ur;
        return (v !== null && v !== undefined && v >= 0) ? v : null;
      });

      return {
        sensorIndex,
        labels: formattedLabels,
        datasets: [
          {
            label: 'TA (°C)',
            data: ta,
            borderColor: '#ff6e2c',
            backgroundColor: 'rgba(255, 110, 44, 0.2)',
            pointBackgroundColor: '#ff6e2c',
            tension: 0.3,
            yAxisID: 'y',
          },
          {
            label: 'TG (°C)',
            data: tg,
            borderColor: '#ff0000',
            backgroundColor: 'rgba(255, 0, 0, 0.15)',
            pointBackgroundColor: '#ff0000',
            tension: 0.3,
            yAxisID: 'y',
          },
          {
            label: 'UR (%)',
            data: ur,
            borderColor: '#0057b8',
            backgroundColor: 'rgba(0, 87, 184, 0.15)',
            pointBackgroundColor: '#0057b8',
            tension: 0.3,
            yAxisID: 'y1',
          }
        ]
      };
    });
  }, [sensoresComDados, baseDados, formattedLabels]);

  // Calcular médias por sensor (posição) e labels das barras, de forma memoizada
  const barStats = useMemo(() => {
    if (!Array.isArray(dadosFiltrados) || dadosFiltrados.length === 0 || sensoresComDados.length === 0) {
      return { labels: [], taMedias: [], tgMedias: [], urMedias: [] };
    }
    const labelsLocal = sensoresComDados.map(i => `Sensor ${i + 1}`).reverse();
    const taMediasLocal = [];
    const tgMediasLocal = [];
    const urMediasLocal = [];
    for (const sensorIndex of sensoresComDados) {
      const taVals = dadosFiltrados.map(item => item.sensores[sensorIndex]?.ta ?? null);
      const tgVals = dadosFiltrados.map(item => item.sensores[sensorIndex]?.tg ?? null);
      const urVals = dadosFiltrados.map(item => item.sensores[sensorIndex]?.ur ?? null);
      taMediasLocal.push(media(taVals));
      tgMediasLocal.push(media(tgVals));
      urMediasLocal.push(media(urVals));
    }
    // Inverter arrays para alinhar com os labels invertidos (sensor 1 embaixo)
    taMediasLocal.reverse();
    tgMediasLocal.reverse();
    urMediasLocal.reverse();
    return { labels: labelsLocal, taMedias: taMediasLocal, tgMedias: tgMediasLocal, urMedias: urMediasLocal };
  }, [dadosFiltrados, sensoresComDados, media]);
  const { labels, taMedias, tgMedias, urMedias } = barStats;

  // Função para gradiente horizontal (barras)
  function getBarGradient(ctx, area, colorStart, colorEnd) {
    const gradient = ctx.createLinearGradient(area.left, 0, area.right, 0);
    gradient.addColorStop(0, colorStart);
    gradient.addColorStop(1, colorEnd);
    return gradient;
  }

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        label: '🌡️ TA (°C) - Temperatura Ambiente',
        data: taMedias,
        backgroundColor: function(context) {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(255, 120, 0, 0.7)';
          return getBarGradient(ctx, chartArea, '#ffe259', '#ff6e2c'); // amarelo para laranja
        },
        borderColor: 'rgba(255, 120, 0, 1)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 45,
      },
      {
        label: '🌾 TG (°C) - Temperatura do Grão',
        data: tgMedias,
        backgroundColor: function(context) {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(255, 0, 0, 0.7)';
          return getBarGradient(ctx, chartArea, '#ffb347', '#ff0000'); // laranja para vermelho
        },
        borderColor: 'rgba(255, 0, 0, 1)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 45,
      },
      {
        label: '💧 UR (%) - Umidade Relativa',
        data: urMedias,
        backgroundColor: function(context) {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(52, 144, 220, 0.7)';
          return getBarGradient(ctx, chartArea, '#b3e0ff', '#0057b8'); // azul claro para azul escuro
        },
        borderColor: 'rgba(52, 144, 220, 1)',
        borderWidth: 1,
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 45,
      }
    ]
  }), [labels, taMedias, tgMedias, urMedias]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // barras horizontais
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 80 // Aumentar padding direito para os labels
      }
    },
    plugins: {
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'right',
        color: '#1c4220',
        font: {
          size: 12,
          weight: 'bold'
        },
        formatter: function(value) {
          return value !== null && value !== undefined ? value.toFixed(1) : '';
        },
        offset: 10
      },
      legend: {
        position: 'top',
        align: 'center',
        labels: { 
          color: '#1c4220', 
          font: { 
            size: window.innerWidth < 768 ? 11 : 13, 
            weight: '600' 
          },
          padding: window.innerWidth < 768 ? 8 : 15,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 12,
          boxHeight: 8
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(143, 173, 21, 0.5)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        mode: 'index',
        intersect: false,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const value = context.parsed.x;
            if (value === null || value === undefined) return null;
            
            let unit = '°C';
            if (context.dataset.label.includes('UR')) {
              unit = '%';
            }
            
            return `${context.dataset.label}: ${value.toFixed(1)}${unit}`;
          }
        }
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'y',
      intersect: false
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { 
          color: 'rgba(28, 66, 32, 0.1)',
          lineWidth: 1,
          drawBorder: false
        },
        ticks: { 
          color: '#1c4220', 
          font: { 
            size: window.innerWidth < 768 ? 10 : 12,
            weight: '500'
          },
          padding: 5,
          callback: function(value) {
            return Number.isInteger(value) ? value : value.toFixed(1);
          }
        },
        border: {
          display: false
        }
      },
      y: {
        grid: { 
          color: 'rgba(28, 66, 32, 0.1)',
          lineWidth: 1,
          drawBorder: false
        },
        ticks: { 
          color: '#1c4220', 
          font: { 
            size: window.innerWidth < 768 ? 10 : 12,
            weight: '600'
          },
          padding: 8
        },
        border: {
          display: false
        }
      }
    },
    elements: {
      bar: {
        borderRadius: 6,
        borderSkipped: false,
      }
    }
  }), []);

  // Early returns devem vir após Hooks
  if (!dados || dados.length === 0) return null;

  // Se não há sensores com dados, exibir mensagem amigável
  if (sensoresComDados.length === 0) {
    return (
      <div className="chart-empty-state">
        <div className="chart-empty-icon">📊</div>
        <h6 className="chart-empty-title">Nenhum sensor com dados</h6>
        <p className="chart-empty-text">Não há dados válidos de sensores para o período selecionado.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      {/* Elemento decorativo de fundo */}
      <div className="chart-background-decoration" />
      
      <div className="chart-content">
        {/* Header do gráfico */}
        <div className="chart-header">
          <h6 className="chart-title">
            📊 Análise de Sensores
            <span className="chart-title-badge">
              Médias por Período
            </span>
          </h6>
          {periodoExibido && (
            <div className="chart-periodo text-muted small mt-1">{periodoExibido}</div>
          )}
        </div>

        {/* Filtros de Data */}
{/*         <Row className="mb-4">
          <Col lg={7} md={12} className="mb-3 mb-lg-0">
            <div className="filter-label">
              Período de Análise
            </div>
            <ButtonGroup size="sm" className="w-100 period-button-group">
              <Button 
                variant={filtroTempo === '24h' ? 'success' : 'outline-success'}
                onClick={() => setFiltroTempo('24h')}
                className={`period-button ${filtroTempo === '24h' ? 'active' : 'inactive'}`}
              >
                24h
              </Button>
              <Button 
                variant={filtroTempo === '7d' ? 'success' : 'outline-success'}
                onClick={() => setFiltroTempo('7d')}
                className={`period-button ${filtroTempo === '7d' ? 'active' : 'inactive'}`}
              >
                7 dias
              </Button>
              <Button 
                variant={filtroTempo === '30d' ? 'success' : 'outline-success'}
                onClick={() => setFiltroTempo('30d')}
                className={`period-button ${filtroTempo === '30d' ? 'active' : 'inactive'}`}
              >
                30 dias
              </Button>
              <Button 
                variant={filtroTempo === 'custom' ? 'success' : 'outline-success'}
                onClick={() => setFiltroTempo('custom')}
                className={`period-button ${filtroTempo === 'custom' ? 'active' : 'inactive'}`}
              >
                Personalizado
              </Button>
            </ButtonGroup>
          </Col>
          {filtroTempo === 'custom' && (
            <Col lg={5} md={12}>
              <div className="filter-label">
                Datas e Horas Personalizadas
              </div>
              <Row>
                <Col xs={6} className="mb-2">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    placeholder="Data início"
                    className="custom-date-input"
                  />
                  <Form.Control
                    type="time"
                    size="sm"
                    value={horaInicio}
                    onChange={(e) => setHoraInicio(e.target.value)}
                    className="custom-time-input mt-1"
                  />
                </Col>
                <Col xs={6}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    placeholder="Data fim"
                    className="custom-date-input"
                  />
                  <Form.Control
                    type="time"
                    size="sm"
                    value={horaFim}
                    onChange={(e) => setHoraFim(e.target.value)}
                    className="custom-time-input mt-1"
                  />
                </Col>
              </Row>
            </Col>
          )}
        </Row> */}
        
        {/* Informações do filtro atual */}
        <div className="mb-4 filter-info">
          <span className="filter-info-icon">📈</span>
          <span className="filter-info-text">
            Exibindo dados de {dadosFiltrados.length} medições
          </span>
          {filtroTempo !== 'custom' && (
            <span className="filter-info-details">
              ({filtroTempo === '24h' ? 'últimas 24 horas' : filtroTempo === '7d' ? 'últimos 7 dias' : 'últimos 30 dias'})
            </span>
          )}
          {filtroTempo === 'custom' && dataInicio && dataFim && (
            <span className="filter-info-details">
              (de {buildLocalDate(dataInicio)?.toLocaleDateString('pt-BR')} até {buildLocalDate(dataFim)?.toLocaleDateString('pt-BR')})
            </span>
          )}
        </div>

        {/* Gráficos de linha por sensor */}
        <div className="mt-4">
          <div className="filter-label mb-2">Séries temporais por sensor</div>
          <Row>
            {[...seriesPorSensor].reverse().map((serie, idx) => (
              <Col key={`sensor-line-${serie.sensorIndex}`} xs={12} md={12} className="mb-3">
                <div className="chart-wrapper" style={{ height: 260 }}>
                  <Line
                    data={{ labels: serie.labels, datasets: serie.datasets }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: {
                            color: '#1c4220',
                            font: { size: window.innerWidth < 768 ? 11 : 12, weight: '600' },
                            usePointStyle: true,
                            pointStyle: 'line'
                          }
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          titleColor: '#ffffff',
                          bodyColor: '#ffffff',
                          borderColor: 'rgba(143, 173, 21, 0.5)',
                          borderWidth: 1,
                          cornerRadius: 8,
                        },
                        datalabels: { display: false }
                      },
                      scales: {
                        x: {
                          grid: { color: 'rgba(28, 66, 32, 0.08)', drawBorder: false },
                          ticks: { color: '#1c4220', font: { size: window.innerWidth < 768 ? 10 : 11, weight: '500' }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 }
                        },
                        y: {
                          type: 'linear', position: 'left', grid: { color: 'rgba(28, 66, 32, 0.1)', drawBorder: false },
                          ticks: { color: '#1c4220' }, title: { display: true, text: '°C', color: '#1c4220' }
                        },
                        y1: {
                          type: 'linear', position: 'right', min: 0, max: 100, grid: { drawOnChartArea: false },
                          ticks: { color: '#1c4220' }, title: { display: true, text: '%', color: '#1c4220' }
                        }
                      },
                      elements: { point: { radius: 2, hoverRadius: 4 }, line: { borderWidth: 2 } }
                    }}
                  />
                </div>
                <div className="small text-muted mt-1">Sensor {serie.sensorIndex + 1}</div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Container do gráfico de barras com altura responsiva */}
        <div className="chart-wrapper mt-4">
          <Bar 
            data={data} 
            options={options} 
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    </div>
  );
}

// Evita re-render quando não há alteração significativa nos dados (mesmo tamanho e mesma última data)
function areEqual(prevProps, nextProps) {
  const prev = prevProps.dados;
  const next = nextProps.dados;
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  const prevLastItem = prev[prev.length - 1];
  const nextLastItem = next[next.length - 1];
  if (prevLastItem?.date !== nextLastItem?.date) return false;
  // Checksum simples dos valores do último item para detectar alterações mesmo com a mesma data
  const sumVals = (item) => {
    const sensores = item?.sensores || [];
    let s = 0;
    for (let i = 0; i < sensores.length; i++) {
      const sn = sensores[i] || {};
      const ta = Number.isFinite(sn.ta) ? sn.ta : 0;
      const tg = Number.isFinite(sn.tg) ? sn.tg : 0;
      const ur = Number.isFinite(sn.ur) ? sn.ur : 0;
      s += ta + tg + ur;
    }
    return s;
  };
  return sumVals(prevLastItem) === sumVals(nextLastItem);
}

export default React.memo(ChartSensores, areEqual);
